import React from "react";
import styled from "@emotion/styled";
import SearchableSelectField from "../../common/dashboard/SearchableSelectField";
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Switch,
  TextField,
} from "@mui/material";
import { ChamberMemberProPaymentScheduleEnum } from "../../../graphql/types";
import { isMemberPaymentSchedule } from "./memberPaymentSchedule";
import { useCmpOrganizationNoun } from "../../../contexts/CmpOrganizationNounContext";
import { applyCmpOrganizationNoun } from "../../../utils/cmpOrganizationNoun";
import {
  InvoicePartyFieldErrors,
  InvoicePartyFields,
} from "../Invoicing/InvoicePartyFields";
import { MemberThirdPartyBillingSnapshot } from "./memberThirdPartyBilling";
import {
  checkboxControlSx,
  choiceControlLabelSx,
  fieldSx,
  FormSelectField,
  FormTextField,
  radioControlSx,
  SectionShell,
  MemberDesignSurface,
} from "./memberWorkflowShared";

/** Claude Design text for the auto-renewal checkbox (Monday D15). */
export const MEMBER_AUTO_RENEW_CARD_LABEL =
  "The member has opted to save their credit card for automatic membership renewals.";

export type MemberBillingDiscountValue = {
  enabled: boolean;
  code: string;
  description: string;
  amount: string;
};

export type MemberBillingValue = {
  fullTimeEmployees: string;
  partTimeEmployees: string;
  membershipTypeId: string;
  paymentSchedule: string;
  paymentMethod: string;
  billingResponsibility: string;
  thirdPartyBillingSnapshot: MemberThirdPartyBillingSnapshot;
  selectedAdditionalCostIds: string[];
  membershipDiscount: MemberBillingDiscountValue;
  autoRenew: boolean;
  emailCommunicationConsentConfirmed: boolean;
};

export type MemberBillingOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type MemberBillingSummary = {
  code: string;
  description: string;
  amountLabel: string;
  /**
   * Spec §7 (proration verbiage): set when annual prorated signup pricing
   * lowers the first-year price, so the step shows BOTH numbers instead of
   * silently substituting the prorated one. Absent (or prorated == base)
   * -> the single amountLabel renders exactly as before.
   */
  proration?: {
    proratedAmountLabel: string;
    regularAmountLabel: string;
  };
};

export type MemberBillingAddOn = {
  id: string;
  code: string;
  name: string;
  description: string;
  amountLabel: string;
};

export type MemberBillingOnboardingFee = {
  code: string;
  description: string;
  amountLabel: string;
};

export type MemberBillingResponsibilityOption = {
  value: string;
  title: string;
  description: string;
  /**
   * Step-4 spec: the "Invoice created for Internal Use Not Sent to Member"
   * sub-lines render bold (members-create-step4.md lines 92-94, 251, 322).
   */
  descriptionBold?: boolean;
};

export const chamberGroupInsuranceAutomaticActivationDescription =
  "The member will automatically become active when this selection is saved. The invoice is for internal use and will not be sent to the member.";

export const chamberGroupInsuranceExistingInvoiceDescription =
  "For a pending member, saving this option replaces the current unpaid membership invoice with an internal Johnson Group invoice and automatically activates the member. If payment activity exists, you’ll be asked to review it first. The invoice is not sent to the member.";

export type MemberBillingErrors = Partial<
  Record<
    | "employeeCount"
    | "fullTimeEmployees"
    | "partTimeEmployees"
    | "membershipTypeId"
    | "paymentSchedule"
    | "membershipDiscountCode"
    | "membershipDiscountDescription"
    | "membershipDiscountAmount"
    | "billingResponsibility"
    | "paymentMethod"
    | "autoRenew"
    | "emailCommunicationConsentConfirmed",
    string
  >
> &
  Partial<
    Record<
      `thirdPartyBillingSnapshot.${keyof MemberThirdPartyBillingSnapshot}`,
      string
    >
  >;

export type MemberBillingAdjustmentConfig = {
  enabled: boolean;
  hasChanges: boolean;
  submitting?: boolean;
  error?: string;
  blockedReason?: string;
  onEnabledChange: (enabled: boolean) => void;
  onSubmit: () => void;
};

export type MemberBillingScheduledChange = {
  statusLabel: string;
  currentMembershipName: string;
  scheduledMembershipName: string;
  effectiveDateLabel: string;
  actionRequiredReason?: string;
};

export type MemberBillingStepProps = {
  appearance?: "default" | "design";
  renewal?: { isTierPricing: boolean; previewTotal?: string };
  /** Update Member Step 5: opens Update Credit Card Information (Monday D15/D18). */
  onUpdateCreditCard?: () => void;
  addOnsReadOnly?: boolean;
  value: MemberBillingValue;
  membershipOptions: MemberBillingOption[];
  /** Shown right under the membership type (Create Member charity number). */
  afterMembershipTypeField?: React.ReactNode;
  selectedMembershipSummary: MemberBillingSummary;
  paymentScheduleOptions: MemberBillingOption[];
  paymentMethodOptions?: MemberBillingOption[];
  paymentMethodTitle?: string;
  paymentMethodDescription?: string;
  billingOptions: MemberBillingResponsibilityOption[];
  addOns: MemberBillingAddOn[];
  oneTimeOnboardingFee?: MemberBillingOnboardingFee;
  addOnsTotalLabel: string;
  invoiceTotalLabel: string;
  // BILL-002/003: monthly memberships are invoiced once for the full
  // membership total (12 credit-card installments), while add-on benefits go
  // on a second full-amount invoice. The parent passes the effective schedule
  // and the two split totals so the copy below can be honest per schedule.
  isMonthlySchedule: boolean;
  membershipInvoiceTotalLabel: string;
  membershipInvoiceTotalInDollars?: number;
  addOnInvoiceTotalLabel: string;
  showPaymentSchedule: boolean;
  showPaymentMethod?: boolean;
  paymentScheduleDescription?: string;
  monthlyPaymentsAvailable?: boolean;
  membershipDiscountEnabled: boolean;
  membershipDiscountAvailable?: boolean;
  autoRenewAvailable?: boolean;
  /**
   * Update-only membership adjustment behavior. Create Member omits this
   * object and retains the existing fully-editable billing workflow.
   */
  membershipAdjustment?: MemberBillingAdjustmentConfig;
  scheduledMembershipChange?: MemberBillingScheduledChange;
  disabled?: boolean;
  membershipPlaceholder?: string;
  /**
   * Create Member: the chamber uses membership tiers, so staff select a
   * "Membership Tier". Omitted, the renewal tier setting applies.
   */
  membershipTierMode?: boolean;
  /** Create Member: Full-Time and Part-Time Employees are required. */
  employeeCountsRequired?: boolean;
  errors?: MemberBillingErrors;
  thirdPartyBillingErrors?: InvoicePartyFieldErrors;
  /** The affiliated payer comes from the Primary Member record (no form). */
  affiliatedPayerFromPrimaryMember?: boolean;
  onFullTimeEmployeesChange: (value: string) => void;
  onPartTimeEmployeesChange: (value: string) => void;
  onMembershipTypeChange: (value: string) => void;
  onPaymentScheduleChange: (value: ChamberMemberProPaymentScheduleEnum) => void;
  onPaymentMethodChange?: (value: string) => void;
  onMembershipDiscountChange: (
    value: Partial<MemberBillingDiscountValue>,
  ) => void;
  onAdditionalCostToggle: (id: string, checked: boolean) => void;
  onBillingResponsibilityChange: (value: string) => void;
  onThirdPartyBillingSnapshotChange: (
    value: MemberThirdPartyBillingSnapshot,
  ) => void;
  onAutoRenewChange: (checked: boolean) => void;
  onEmailConsentChange: (checked: boolean) => void;
};

const centeredControlLabelSx = {
  ...choiceControlLabelSx,
  alignItems: "center",
  "& .MuiFormControlLabel-label": {
    minWidth: 0,
    lineHeight: 1.5,
    paddingTop: 0,
  },
};

const fullWidthChoiceControlLabelSx = {
  ...choiceControlLabelSx,
  alignItems: "flex-start",
  width: "100%",
  "& .MuiFormControlLabel-label": {
    minWidth: 0,
    paddingTop: "6px",
    width: "100%",
  },
};

type InlineBillingTextFieldProps = {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  disabled?: boolean;
  error?: string;
  onChange: (value: string) => void;
};

const InlineBillingTextField = ({
  id,
  label,
  value,
  placeholder,
  disabled,
  error,
  onChange,
}: InlineBillingTextFieldProps) => (
  <TextField
    id={id}
    aria-label={label}
    value={value}
    onChange={(event) => onChange(event.target.value)}
    placeholder={placeholder}
    fullWidth
    error={!!error}
    helperText={error}
    disabled={disabled}
    sx={fieldSx}
  />
);

const toNumericInput = (value: string) => value.replace(/[^0-9]/g, "");

const toCurrencyInput = (value: string) => value.replace(/[^0-9.]/g, "");

const getPaymentScheduleLabel = (option: MemberBillingOption) =>
  option.label || option.value;

const BillingDesignSurface = styled(MemberDesignSurface)({
  "& .text-lg": { fontSize: 14, lineHeight: "1.55" },
  "& .text-base": { fontSize: 14 },
  "& .text-2xl": { fontSize: 16 },
  "& .text-3xl": { fontSize: 22 },
  "& .text-4xl": { fontSize: 24 },
  "& #member-size .cmp-dashboard-density-form-field > label": {
    lineHeight: "normal",
  },
  "& #member-size .MuiOutlinedInput-input": {
    height: "auto",
    boxSizing: "border-box",
    border: "1px solid transparent",
    lineHeight: "normal",
  },
  "&.member-billing-locked-step4 #member-size > div:first-of-type h3, &.member-billing-locked-step4 #type-of-membership > div:first-of-type h3, &.member-billing-locked-step4 #membership-payment-option > div:first-of-type h3": {
    lineHeight: "22.5px",
  },
  "&.member-billing-locked-step4 #member-size > div:first-of-type > p, &.member-billing-locked-step4 #type-of-membership > div:first-of-type > p, &.member-billing-locked-step4 #membership-payment-option > div:first-of-type > p": {
    lineHeight: "18.5px !important",
  },
  "&.member-billing-locked-step4 #member-size .cmp-dashboard-density-form-field > label, &.member-billing-locked-step4 #member-size .MuiOutlinedInput-input": {
    lineHeight: "18.5px",
  },
  "&.member-billing-locked-step4 .MuiInputBase-input.Mui-disabled": {
    color: "#0A0A0A",
    WebkitTextFillColor: "#0A0A0A",
    opacity: 1,
  },
  "&.member-billing-locked-step4 .MuiInputBase-input.Mui-disabled::placeholder": {
    color: "#9E9E9E",
    WebkitTextFillColor: "#9E9E9E",
    opacity: 1,
  },
  "&.member-billing-locked-step4 #type-of-membership .member-billing-type-field > p": {
    color: "#0A0A0A",
  },
  "&.member-billing-locked-step4 #member-size label .text-red-500": {
    color: "#E24C4C",
  },
  "&.member-billing-locked-step4 #type-of-membership .member-billing-type-field > p, &.member-billing-locked-step4 #type-of-membership .member-billing-investment-row > div:nth-of-type(2) p, &.member-billing-locked-step4 #type-of-membership #step4-membership-type, &.member-billing-locked-step4 #membership-payment-option .MuiFormControlLabel-root .MuiFormControlLabel-label > span": {
    lineHeight: "18.5px",
  },
  "& #type-of-membership .member-billing-type-field > p": {
    lineHeight: "normal",
  },
  "& #type-of-membership > .mt-8": {
    marginTop: 0,
    borderTop: "1px solid #ECECEC",
    paddingTop: 16,
  },
  "& #type-of-membership .member-billing-investment-row": {
    alignItems: "start",
  },
  "& #type-of-membership .member-billing-investment-row p": {
    lineHeight: "normal",
  },
  "& #type-of-membership .member-billing-investment-row > :last-child": {
    alignSelf: "center",
  },
  "& #membership-discount > .grid": { marginTop: 0 },
  "& #membership-payment-option .MuiRadioGroup-root": {
    display: "flex",
    flexDirection: "row",
    gap: "16px 60px",
  },
  "& #membership-payment-option .MuiRadioGroup-root .font-bold": {
    fontWeight: 400,
  },
  "& #membership-payment-option .MuiFormControlLabel-root": {
    alignItems: "center",
    gap: 9,
  },
  "& #membership-payment-option .MuiFormControlLabel-root .MuiFormControlLabel-label, & #membership-payment-option .text-lg": {
    lineHeight: "normal",
  },
  "& #membership-payment-option .MuiRadio-root": { marginTop: 0 },
  "& #billing-details .MuiFormControlLabel-label > span > span:first-of-type": {
    fontWeight: 600,
  },
  "& #billing-details .MuiFormControlLabel-label > span > span:nth-of-type(2)":
    { fontSize: 13, marginTop: 2, lineHeight: 1.5 },
  "& #additional-membership-add-ons .mt-1.leading-6":
    { fontSize: 13, marginTop: 3, lineHeight: 1.5 },
  "& #type-of-membership .mt-1.leading-6":
    { fontSize: 12, color: "#727272", lineHeight: "normal" },
  "& #additional-membership-add-ons > .mt-5 .text-sm":
    { fontSize: 12, color: "#727272", lineHeight: 1.5 },
  "& #additional-membership-add-ons > .border-t": { borderTop: 0 },
  "& #additional-membership-add-ons label.grid, & #additional-membership-add-ons div.grid.border-b":
    { paddingTop: 6, paddingBottom: 6, borderBottom: 0, gap: 16 },
  "& #additional-membership-add-ons .MuiCheckbox-root": {
    padding: 0,
    marginTop: 2,
  },
  "& #additional-membership-add-ons > .mt-5": {
    marginTop: 0,
    borderTop: "1px solid #ECECEC",
    paddingTop: 16,
  },
  "& #membership-billing-confirmations": {
    paddingLeft: 0,
    paddingRight: 0,
    gap: 12,
  },
  "& #membership-billing-confirmations span.text-base": {
    fontSize: 14,
    lineHeight: 1.5,
  },
  "& #total-new-member-invoice": {
    borderBottom: 0,
    borderTop: "1px solid #ECECEC",
    paddingTop: 20,
    paddingBottom: 0,
  },
  "& #total-new-member-invoice h3": { fontSize: 18 },
  "& #total-new-member-invoice p.mt-3, & #total-new-member-invoice p.mt-2": {
    marginTop: 6,
    fontSize: 14,
    lineHeight: 1.55,
  },
  "@media (min-width: 768px)": {
    "& #membership-discount > .grid": {
      gridTemplateColumns:
        "minmax(100px,0.5fr) minmax(0,2fr) minmax(100px,0.5fr)",
    },
    "& #type-of-membership > .mt-8": {
      gridTemplateColumns: "auto minmax(0,1fr) auto",
      gap: 18,
    },
    "& #additional-membership-add-ons label.grid, & #additional-membership-add-ons div.grid.border-b":
      { gridTemplateColumns: "18px 60px minmax(0,1fr) 110px" },
  },
});

const MemberBillingStep = ({
  value,
  appearance = "default",
  renewal,
  onUpdateCreditCard,
  addOnsReadOnly = false,
  membershipOptions,
  afterMembershipTypeField,
  selectedMembershipSummary,
  paymentScheduleOptions,
  paymentMethodOptions = [],
  paymentMethodTitle = "Select Payment Method For The One-Time Onboarding Fee",
  paymentMethodDescription = "The onboarding fee must be paid even when the membership itself has no charge. Select one of the payment methods enabled in Chamber Set Up.",
  billingOptions,
  addOns,
  oneTimeOnboardingFee,
  addOnsTotalLabel,
  invoiceTotalLabel,
  isMonthlySchedule,
  membershipInvoiceTotalLabel,
  membershipInvoiceTotalInDollars,
  addOnInvoiceTotalLabel,
  showPaymentSchedule,
  showPaymentMethod = false,
  paymentScheduleDescription,
  monthlyPaymentsAvailable = true,
  membershipDiscountEnabled,
  membershipDiscountAvailable = true,
  autoRenewAvailable = true,
  membershipAdjustment,
  scheduledMembershipChange,
  disabled,
  membershipPlaceholder = "Select Membership Type",
  membershipTierMode,
  employeeCountsRequired = false,
  errors = {},
  thirdPartyBillingErrors = {},
  affiliatedPayerFromPrimaryMember = false,
  onFullTimeEmployeesChange,
  onPartTimeEmployeesChange,
  onMembershipTypeChange,
  onPaymentScheduleChange,
  onPaymentMethodChange,
  onMembershipDiscountChange,
  onAdditionalCostToggle,
  onBillingResponsibilityChange,
  onThirdPartyBillingSnapshotChange,
  onAutoRenewChange,
  onEmailConsentChange,
}: MemberBillingStepProps) => {
  const noun = useCmpOrganizationNoun();
  const billingControlsDisabled =
    !!disabled ||
    !!membershipAdjustment?.submitting ||
    (!!membershipAdjustment && !membershipAdjustment.enabled);
  const addOnsAreReadOnly = !!membershipAdjustment || addOnsReadOnly;
  const showAddOnMembershipChangeCopy =
    addOnsAreReadOnly && (!!renewal || !!membershipAdjustment);
  const isReadOnlyMembershipDetails =
    !renewal &&
    appearance === "design" &&
    billingControlsDisabled &&
    (addOnsReadOnly || !!membershipAdjustment);
  const isStandaloneReadOnlyMembershipDetails =
    isReadOnlyMembershipDetails && !membershipAdjustment;
  const usesThirdPartyBilling =
    value.billingResponsibility === "affiliatedPrimaryCompany" ||
    value.billingResponsibility === "otherThirdParty" ||
    value.billingResponsibility === "chamberGroupInsurance";
  // Update Member Step 4 (view only) and Step 5 (renewal) confirm the saved
  // membership; they share the design's "Confirm ..." copy and asterisks.
  const confirmsSavedMembership = isStandaloneReadOnlyMembershipDetails || !!renewal;
  const effectivePaymentScheduleDescription =
    paymentScheduleDescription ||
    (confirmsSavedMembership
      ? "Confirm the member payment option below. Monthly payments are made via credit card only."
      : usesThirdPartyBilling
      ? "Select the membership billing schedule below. Third-party billing does not require the member to provide a credit card."
      : "Select the member payment option below. Monthly payments are made via credit card only.");
  const showSeparateAddOnInvoice =
    !renewal && !membershipAdjustment && value.selectedAdditionalCostIds.length > 0;
  const isCreateFlow = !renewal && !membershipAdjustment && !addOnsReadOnly;
  const usesMembershipTiers = membershipTierMode ?? !!renewal?.isTierPricing;
  const employeeCountRequired =
    !!renewal || isStandaloneReadOnlyMembershipDetails || employeeCountsRequired;

  const BillingRoot = appearance === "design" ? BillingDesignSurface : "div";
  return (
    <BillingRoot
      className={`${appearance === "design" ? "space-y-6" : "space-y-10"}${
        isStandaloneReadOnlyMembershipDetails
          ? " member-billing-locked-step4"
          : ""
      }`}
    >
      <SectionShell
        id="member-size"
        title="Member Size Matters"
        descriptionLineHeight="normal"
        description={
          // Update Member Form.dc.html:1038 (Step 4, view only) and :1178 (Step 5).
          isStandaloneReadOnlyMembershipDetails
            ? "The system automatically populates the numbers from full-time and part-time employees entered during new memberships and renewals."
            : confirmsSavedMembership
            ? "Confirm the size of this member, edit the number of full-time and part-time employees, if it has changed."
            : appearance === "design"
            ? "Get to know the size of this member enter in the number of full-time and part-time employees"
            : "Get to know the size of this member. Enter the number of full-time and part-time employees."
        }
        variant={appearance === "design" ? "design" : "card"}
        contentGap={16}
        className={
          appearance !== "design" &&
          membershipAdjustment &&
          !membershipAdjustment.enabled
            ? "!border-gray-300 !bg-gray-100"
            : ""
        }
      >
        <div
          className={appearance === "design"
            ? "grid grid-cols-1 gap-[20px] md:grid-cols-2"
            : "grid grid-cols-1 gap-x-8 md:grid-cols-2"}
        >
          <FormTextField
            id="step4-full-time-employees"
            label="Full-Time Employees"
            required={employeeCountRequired}
            value={value.fullTimeEmployees}
            onChange={(nextValue) =>
              onFullTimeEmployeesChange(toNumericInput(nextValue))
            }
            placeholder={
              appearance === "design"
                ? "00"
                : "Enter the number of full time employees in the company"
            }
            error={errors.fullTimeEmployees || errors.employeeCount}
            disabled={billingControlsDisabled}
          />
          <FormTextField
            id="step4-part-time-employees"
            label="Part-Time Employees"
            required={employeeCountRequired}
            value={value.partTimeEmployees}
            onChange={(nextValue) =>
              onPartTimeEmployeesChange(toNumericInput(nextValue))
            }
            placeholder={
              appearance === "design"
                ? "00"
                : "Enter the number of part time employees in the company"
            }
            error={errors.partTimeEmployees}
            disabled={billingControlsDisabled}
          />
        </div>
      </SectionShell>

      <SectionShell
        id="type-of-membership"
        title="Type of Membership"
        descriptionLineHeight="normal"
        description={confirmsSavedMembership
          ? "Confirm the membership that has been auto-populated. This is what will be used to send out the membership renewal invoice."
          : isCreateFlow
          ? "Select the membership this member has chosen from the drop-down below. The membership investment will automatically populate."
          : "Select the membership this member has chosen from the drop-down below. The membership investment will automatically populate."}
        variant={appearance === "design" ? "design" : "card"}
        contentGap={16}
        className={
          appearance !== "design" &&
          membershipAdjustment &&
          !membershipAdjustment.enabled
            ? "!border-gray-300 !bg-gray-100"
            : ""
        }
      >
        {appearance === "design" ? (
          <div className="member-billing-type-field">
            <p className="mb-[6px] text-sm font-bold">
              {usesMembershipTiers ? "Membership Tier" : "Membership Type"}<span className="text-[#E24C4C]">*</span>
            </p>
            <p className="mb-[6px] text-xs text-[#727272]" style={{ fontSize: addOnsAreReadOnly ? 14 : 12 }}>
              {isCreateFlow && membershipTierMode
                ? `Your ${noun.singular} has membership tiers enabled — select this member’s tier from the dropdown below`
                : renewal && usesMembershipTiers
                ? `Your ${noun.singular} has membership tiers enabled — confirm the membership tier from the dropdown below.`
                : `Membership type is automatically populated from your ${noun.singular} set up`}
            </p>
            {isReadOnlyMembershipDetails ? (
              <div
                id="step4-membership-type"
                role="textbox"
                aria-label="Membership Type"
                aria-readonly="true"
                className="rounded-[8px] border border-[#ECECEC] bg-[#F7F7F8] px-[12px] py-[11px] text-[14px] leading-[normal]"
              >
                {membershipOptions.find(
                  (option) => option.value === value.membershipTypeId,
                )?.label || membershipPlaceholder}
              </div>
            ) : (
              <SearchableSelectField
                id="step4-membership-type"
                value={value.membershipTypeId}
                onChange={onMembershipTypeChange}
                options={membershipOptions}
                placeholder={usesMembershipTiers ? "Select Membership Tier" : membershipPlaceholder}
                searchPlaceholder={usesMembershipTiers ? "Search Membership Tier" : "Search Membership Type"}
                error={errors.membershipTypeId}
                disabled={billingControlsDisabled}
              />
            )}
          </div>
        ) : (
          <>
            <FormSelectField
              id="step4-membership-type"
              label="Membership Type"
              value={value.membershipTypeId}
              onChange={onMembershipTypeChange}
              options={membershipOptions}
              placeholder={membershipPlaceholder}
              description={`Membership type is automatically populated from your ${noun.singular} set up`}
              error={errors.membershipTypeId}
              required
              disabled={billingControlsDisabled}
            />
          </>
        )}
        {afterMembershipTypeField}
        <div className="member-billing-investment-row mt-8 grid grid-cols-1 items-end gap-4 text-sm text-black lg:grid-cols-[120px_minmax(0,1fr)_220px]">
          <div>
            <p className="text-2xl font-bold text-black">
              {selectedMembershipSummary.code || "-"}
            </p>
          </div>
          <div>
            <p className="text-base font-bold text-black">
              Company Membership Investment Amount
            </p>
            <p className="mt-1 leading-6" style={appearance === "design" ? { marginTop: 2, fontSize: addOnsAreReadOnly ? 14 : 12 } : undefined}>
              {(appearance !== "design" &&
                selectedMembershipSummary.description) ||
                "Investment amount is automatically populated based on the membership selected."}
            </p>
          </div>
          {selectedMembershipSummary.proration ? (
            <div className="text-black lg:text-right">
              <p className="text-3xl font-bold">
                {selectedMembershipSummary.proration.proratedAmountLabel}
              </p>
              <p className="text-base font-bold leading-6">
                for your first year, then{" "}
                {selectedMembershipSummary.proration.regularAmountLabel} /annual
                afterwards
              </p>
            </div>
          ) : (
            <p className="text-3xl font-bold text-black lg:text-right">
              {selectedMembershipSummary.amountLabel}
            </p>
          )}
        </div>
        {selectedMembershipSummary.proration && (
          <p className="mt-3 text-sm leading-6 text-black">
            Regular price:{" "}
            {selectedMembershipSummary.proration.regularAmountLabel} /annual.
            Your {noun.singular} has prorated your first-year membership fee based on
            the month you are joining — you will only be charged{" "}
            {selectedMembershipSummary.proration.proratedAmountLabel} for this
            year.
          </p>
        )}
        {membershipAdjustment?.enabled && (
          <div className="mt-8 flex justify-center">
            <Button
              type="button"
              variant="contained"
              disabled={
                !!disabled ||
                membershipAdjustment.submitting ||
                !membershipAdjustment.hasChanges
              }
              onClick={membershipAdjustment.onSubmit}
              sx={{
                backgroundColor: "#E4A927",
                color: "white",
                fontWeight: 900,
                minWidth: 230,
                textTransform: "none",
                "&:hover": { backgroundColor: "#C58C16" },
              }}
            >
              {membershipAdjustment.submitting
                ? "Updating Membership Type..."
                : "Update Membership Type"}
            </Button>
          </div>
        )}
      </SectionShell>

      {membershipAdjustment && (
        <SectionShell
          id="adjust-membership-type"
          title="Adjust Membership Type"
          description={
            membershipAdjustment.enabled
              ? "Make the required changes, then select Update Membership Type. The current invoice and payment history will determine whether the change is applied now or scheduled for the next renewal."
              : "Enable editing to update this member's membership and billing details. Add-on benefits are view-only and will not be charged again."
          }
          variant={appearance === "design" ? "design" : "card"}
          contentGap={16}
        >
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <span className="text-sm font-bold text-black">
              Allow Membership Type Change
            </span>
            <Switch
              checked={membershipAdjustment.enabled}
              disabled={
                disabled ||
                membershipAdjustment.submitting ||
                !!membershipAdjustment.blockedReason
              }
              inputProps={{ "aria-label": "Allow membership type change" }}
              onChange={(event) =>
                membershipAdjustment.onEnabledChange(event.target.checked)
              }
            />
            <span
              className={`text-xs font-bold uppercase tracking-wide ${
                membershipAdjustment.enabled
                  ? "text-green-700"
                  : "text-gray-500"
              }`}
            >
              {membershipAdjustment.enabled ? "Enabled" : "Disabled"}
            </span>
          </div>

          {membershipAdjustment.error && (
            <FormHelperText error className="mt-3">
              {membershipAdjustment.error}
            </FormHelperText>
          )}
          {membershipAdjustment.blockedReason && (
            <FormHelperText className="mt-3 !text-amber-800">
              {membershipAdjustment.blockedReason}
            </FormHelperText>
          )}
        </SectionShell>
      )}

      {scheduledMembershipChange && (
        <SectionShell
          id="scheduled-membership-change"
          title="Scheduled Membership Change"
          description="This member already has a membership change scheduled for their existing renewal date."
          variant={appearance === "design" ? "design" : "card"}
          contentGap={16}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-amber-900">
              {scheduledMembershipChange.statusLabel}
            </span>
            <span className="text-sm font-bold text-black">
              Effective {scheduledMembershipChange.effectiveDateLabel}
            </span>
          </div>
          <div className="mt-5 grid grid-cols-1 gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4 sm:grid-cols-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-gray-600">
                Current Membership
              </p>
              <p className="mt-1 font-bold text-black">
                {scheduledMembershipChange.currentMembershipName}
              </p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wide text-gray-600">
                Scheduled Membership
              </p>
              <p className="mt-1 font-bold text-black">
                {scheduledMembershipChange.scheduledMembershipName}
              </p>
            </div>
          </div>
          {scheduledMembershipChange.actionRequiredReason && (
            <p className="mt-4 text-sm font-bold leading-6 text-red-700">
              Action required: {scheduledMembershipChange.actionRequiredReason}
            </p>
          )}
        </SectionShell>
      )}

      {showPaymentSchedule && (
        <SectionShell
          id="membership-payment-option"
          title={confirmsSavedMembership ? (
            <>Membership Payment Option <span className="text-[#E24C4C]">*</span></>
          ) : "Membership Payment Option"}
          descriptionLineHeight={appearance === "design" ? "normal" : undefined}
          description={effectivePaymentScheduleDescription}
          variant={appearance === "design" ? "design" : "card"}
          contentGap={16}
        >
          <RadioGroup
            row
            value={value.paymentSchedule}
            onChange={(event) => {
              if (isMemberPaymentSchedule(event.target.value)) {
                onPaymentScheduleChange(event.target.value);
              }
            }}
            className="grid grid-cols-1 gap-x-12 gap-y-2 sm:grid-cols-2"
          >
            {paymentScheduleOptions.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                disabled={billingControlsDisabled || option.disabled}
                sx={centeredControlLabelSx}
                control={<Radio sx={radioControlSx} />}
                label={
                  <span className="text-lg font-bold text-black">
                    {getPaymentScheduleLabel(option)}
                  </span>
                }
              />
            ))}
          </RadioGroup>
          {errors.paymentSchedule && (
            <FormHelperText error>{errors.paymentSchedule}</FormHelperText>
          )}
        </SectionShell>
      )}

      {membershipDiscountEnabled && (
        <SectionShell
          id="membership-discount"
          title="Membership Discount Option is Enabled for (Internal Use Only)"
          description="To apply a membership discount for this member, check box, enter the discount code and description as you would like it to appear on the invoice, then enter the discount amount. The discount will automatically be applied and shown as a separate line item on the invoice. If not selected no discount description or amount will show on invoice."
          variant={appearance === "design" ? "design" : "card"}
          contentGap={16}
        >
          <FormControlLabel
            sx={centeredControlLabelSx}
            control={
              <Checkbox
                checked={value.membershipDiscount.enabled}
                disabled={
                  billingControlsDisabled ||
                  (!membershipDiscountAvailable &&
                    !value.membershipDiscount.enabled)
                }
                onChange={(event) =>
                  onMembershipDiscountChange({
                    enabled: membershipDiscountAvailable
                      ? event.target.checked
                      : false,
                  })
                }
                sx={checkboxControlSx}
              />
            }
            label={
              <span className="text-sm font-bold text-black">
                Select to allow this member to receive a membership discount
              </span>
            }
          />
          {!membershipDiscountAvailable && (
            <FormHelperText className="!text-amber-800">
              Membership discounts are no longer enabled in {noun.Singular} Set Up.
              Clear the saved discount to continue; it cannot be re-enabled
              here.
            </FormHelperText>
          )}
          <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-[240px_minmax(0,1fr)_240px]">
            <InlineBillingTextField
              id="membership-discount-code"
              label="Membership Discount Code"
              value={value.membershipDiscount.code}
              onChange={(nextValue) =>
                onMembershipDiscountChange({ code: nextValue })
              }
              placeholder={
                appearance === "design"
                  ? "Code"
                  : "Enter Membership Discount Code"
              }
              disabled={
                billingControlsDisabled ||
                !membershipDiscountAvailable ||
                !value.membershipDiscount.enabled
              }
              error={errors.membershipDiscountCode}
            />
            <InlineBillingTextField
              id="membership-discount-description"
              label="Membership Discount Description"
              value={value.membershipDiscount.description}
              onChange={(nextValue) =>
                onMembershipDiscountChange({ description: nextValue })
              }
              placeholder={
                appearance === "design"
                  ? "Enter Membership Discount"
                  : "Enter Membership Discount Description"
              }
              disabled={
                billingControlsDisabled ||
                !membershipDiscountAvailable ||
                !value.membershipDiscount.enabled
              }
              error={errors.membershipDiscountDescription}
            />
            <InlineBillingTextField
              id="membership-discount-amount"
              label="Amount"
              value={value.membershipDiscount.amount}
              onChange={(nextValue) =>
                onMembershipDiscountChange({
                  amount: toCurrencyInput(nextValue),
                })
              }
              placeholder={
                appearance === "design" ? "0" : "Enter Discount Amount"
              }
              disabled={
                billingControlsDisabled ||
                !membershipDiscountAvailable ||
                !value.membershipDiscount.enabled
              }
              error={errors.membershipDiscountAmount}
            />
          </div>
        </SectionShell>
      )}

      {oneTimeOnboardingFee && !membershipAdjustment && (
        <SectionShell
          id="one-time-onboarding-fee"
          title="One-Time Onboarding Fee For New Members"
          description="This taxable fee is charged once for this new membership application. Membership discounts and proration do not reduce it, and it is not included in future renewals. For monthly memberships, the full onboarding fee is collected with the first payment rather than split across installments."
          variant={appearance === "design" ? "design" : "card"}
          contentGap={16}
        >
          <div className="grid grid-cols-1 items-start gap-4 border-y border-gray-200 py-5 text-sm text-black md:grid-cols-[160px_minmax(0,1fr)_220px]">
            <span className="font-bold">
              {oneTimeOnboardingFee.code || "ONBOARDING"}
            </span>
            <span>
              <span className="block font-bold">
                {oneTimeOnboardingFee.description || "One-Time Onboarding Fee"}
              </span>
              <span className="mt-1 block leading-6">
                Charged in full on this application only.
              </span>
            </span>
            <span className="text-lg font-bold md:text-right">
              {oneTimeOnboardingFee.amountLabel}
            </span>
          </div>
        </SectionShell>
      )}

      <SectionShell
        id="additional-membership-add-ons"
        title="Additional Membership Benefit Add-ons and Fees"
        description={
          showAddOnMembershipChangeCopy
            ? `Enabled add-on benefits from ${noun.Singular} Set Up are shown for reference. Checked benefits are currently attached to this member. They cannot be changed here or added to a replacement membership invoice.`
            : `Only the additional benefits you have created and enabled in your ${noun.Singular} Setup will appear below. You can update, enable, or disable these options at any time by returning to your ${noun.Singular} Setup.`
        }
        variant={appearance === "design" ? "design" : "card"}
        contentGap={16}
      >
        {addOnsAreReadOnly && !isStandaloneReadOnlyMembershipDetails && (
          <span className="mb-4 inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-gray-700">
            View only
          </span>
        )}
        <div className="border-t border-gray-200">
          {addOns.length === 0 ? (
            <div className="py-5 text-sm text-black">
              {addOnsAreReadOnly
                ? "No enabled or previously selected additional membership benefit add-ons are available."
                : `No add-on benefits are set up yet. To add one, go to ${noun.Singular} Set Up > Membership Settings.`}
            </div>
          ) : (
            addOns.map((addOn) => {
              const addOnRow = (
                <>
                  <span className="flex items-start md:justify-center">
                    <Checkbox
                      checked={value.selectedAdditionalCostIds.includes(
                        addOn.id,
                      )}
                      disabled={disabled || addOnsAreReadOnly}
                      onChange={(event) =>
                        onAdditionalCostToggle(addOn.id, event.target.checked)
                      }
                      sx={checkboxControlSx}
                    />
                  </span>
                  <span className="font-bold">{addOn.code || "-"}</span>
                  <span>
                    <span className="block font-bold">{addOn.name}</span>
                    <span className="mt-1 block leading-6">
                      {addOn.description ||
                        "Additional membership benefit add-on."}
                    </span>
                  </span>
                  <span className="font-bold md:text-right">
                    {addOn.amountLabel}
                  </span>
                </>
              );
              const rowClassName =
                "grid grid-cols-1 gap-4 border-b border-gray-200 py-5 text-sm text-black md:grid-cols-[56px_90px_minmax(0,1fr)_180px]";

              return addOnsAreReadOnly ? (
                <div key={addOn.id} className={rowClassName}>
                  {addOnRow}
                </div>
              ) : (
                <label
                  key={addOn.id}
                  className={`${rowClassName} cursor-pointer`}
                >
                  {addOnRow}
                </label>
              );
            })
          )}
        </div>
        <div className="mt-5 grid grid-cols-1 gap-3 text-black md:grid-cols-[minmax(0,1fr)_220px]">
          <div>
            <p className="text-base font-bold">
              {showAddOnMembershipChangeCopy
                ? "Current Benefit Add-ons Investment"
                : "Total Benefit Add-ons Investment"}
            </p>
            <p className="mt-1 text-sm">
              {showAddOnMembershipChangeCopy
                ? "Only checked, currently attached add-ons are included in this reference total. Add-ons are excluded from every invoice produced by this membership change."
                : "Investment amount is automatically populated based on the add-on benefits selected."}
            </p>
          </div>
          <p className="text-3xl font-bold md:text-right">{addOnsTotalLabel}</p>
        </div>
      </SectionShell>

      <SectionShell
        id="billing-details"
        title={confirmsSavedMembership ? (
          <>Select the Billing Details the Member has chosen. <span className="text-[#E24C4C]">*</span></>
        ) : "Select the Billing Details the Member has chosen."}
        description={
          confirmsSavedMembership
            ? "Confirm who will be paying for this members membership must select one option below:"
            : appearance === "design"
            ? "Select who will be paying for this members membership must select one option below:"
            : "Select who will be paying for this member's membership. Must select one option below:"
        }
        variant={appearance === "design" ? "design" : "card"}
        contentGap={16}
      >
        <RadioGroup
          value={value.billingResponsibility}
          onChange={(event) =>
            onBillingResponsibilityChange(event.target.value)
          }
          className={appearance === "design" ? "gap-[16px]" : "space-y-3"}
        >
          {billingOptions.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              disabled={billingControlsDisabled}
              sx={fullWidthChoiceControlLabelSx}
              control={<Radio sx={radioControlSx} />}
              label={
                <span className="block text-black">
                  <span className="block text-sm font-bold">
                    {option.title}
                  </span>
                  <span
                    className={`mt-1 block text-sm leading-6${
                      option.descriptionBold ? " font-bold" : ""
                    }`}
                  >
                    {option.description}
                  </span>
                </span>
              }
            />
          ))}
        </RadioGroup>
        {errors.billingResponsibility && (
          <FormHelperText error>{errors.billingResponsibility}</FormHelperText>
        )}
        {/* Update Member (design (13) Step 5) bills the affiliated Primary
            Member company from its own record, so it hides the payer form for
            that option. Create Member still collects the payer here. */}
        {(value.billingResponsibility === "otherThirdParty" ||
          (value.billingResponsibility === "affiliatedPrimaryCompany" &&
            !affiliatedPayerFromPrimaryMember)) && (
          <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-5">
            <h3 className="text-base font-bold text-black">
              Third-party payer company and address
            </h3>
            <p className="mb-5 mt-1 text-sm leading-6 text-gray-700">
              These private billing details appear on the invoice and determine
              where payment requests are sent. They are not shown in public
              member directories.
            </p>
            <InvoicePartyFields
              disabled={billingControlsDisabled}
              errors={thirdPartyBillingErrors}
              idPrefix="member-third-party-payer"
              onChange={onThirdPartyBillingSnapshotChange}
              value={value.thirdPartyBillingSnapshot}
            />
          </div>
        )}
      </SectionShell>

      {showPaymentMethod && (
        <SectionShell
          id="membership-onboarding-fee-payment-method"
          title={paymentMethodTitle}
          description={applyCmpOrganizationNoun(paymentMethodDescription, noun)}
          variant={appearance === "design" ? "design" : "card"}
          contentGap={16}
        >
          {paymentMethodOptions.length > 0 ? (
            <RadioGroup
              value={value.paymentMethod}
              onChange={(event) => onPaymentMethodChange?.(event.target.value)}
              className="grid grid-cols-1 gap-x-12 gap-y-2 sm:grid-cols-2"
            >
              {paymentMethodOptions.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  disabled={billingControlsDisabled || option.disabled}
                  sx={centeredControlLabelSx}
                  control={<Radio sx={radioControlSx} />}
                  label={
                    <span className="text-base font-bold text-black">
                      {option.label}
                    </span>
                  }
                />
              ))}
            </RadioGroup>
          ) : (
            <p className="text-sm leading-6 text-amber-800">
              No eligible payment methods are enabled in {noun.Singular} Set Up.
            </p>
          )}
          {errors.paymentMethod && (
            <FormHelperText error>{errors.paymentMethod}</FormHelperText>
          )}
        </SectionShell>
      )}

      <div
        id="membership-billing-confirmations"
        className="flex flex-col gap-4 px-6 text-black"
      >
        {/* Monday D15/D18, Update Member Form (15) D:126271 and D:139503, CMP
            Dashboard (15) D:718335: one sentence on Create Member Step 4 and
            Update Member Steps 4 and 5. Step 5 adds Update Credit Card
            Information, active only while auto-renew is ticked (Moeen's spec
            D15, decision B5, Monday 13242995751). */}
        <div className="flex flex-wrap items-center gap-4">
          <FormControlLabel
            sx={centeredControlLabelSx}
            control={
              <Checkbox
                checked={value.autoRenew}
                disabled={
                  billingControlsDisabled ||
                  (!autoRenewAvailable && !value.autoRenew)
                }
                onChange={(event) => onAutoRenewChange(event.target.checked)}
                sx={checkboxControlSx}
              />
            }
            label={
              <span className="text-[14px] leading-[1.5] text-[#0A0A0A]">
                {MEMBER_AUTO_RENEW_CARD_LABEL}
              </span>
            }
          />
          {renewal && onUpdateCreditCard && (
            <button
              type="button"
              disabled={!value.autoRenew}
              title={value.autoRenew ? undefined : "Tick the auto-renew box to update the credit card."}
              onClick={onUpdateCreditCard}
              className="shrink-0 whitespace-nowrap rounded-[8px] border border-[#0091FF] bg-[#0091FF] px-[14px] py-[7px] text-[14px] font-semibold text-white hover:border-[#0077D6] hover:bg-[#0077D6] disabled:cursor-not-allowed disabled:border-[#CFCFCF] disabled:bg-[#CFCFCF]"
            >
              Update Credit Card Information
            </button>
          )}
        </div>
        {renewal && !value.autoRenew && (
          <span className="text-[14px] font-semibold text-[#9E9E9E]">Auto-Renew Disabled</span>
        )}
        {!autoRenewAvailable && (
          <FormHelperText className="!text-amber-800">
            Automatic renewal is not enabled in {noun.Singular} Set Up.
            {value.autoRenew
              ? " Clear the saved auto-renew choice to continue."
              : " It cannot be enabled for this membership change."}
          </FormHelperText>
        )}
        {errors.autoRenew && (
          <FormHelperText error>{errors.autoRenew}</FormHelperText>
        )}
        <FormControlLabel
          sx={fullWidthChoiceControlLabelSx}
          control={
            <Checkbox
              checked={value.emailCommunicationConsentConfirmed}
              disabled={billingControlsDisabled}
              onChange={(event) => onEmailConsentChange(event.target.checked)}
              sx={checkboxControlSx}
            />
          }
          label={
            <span className="block text-base leading-7 text-black">
              I confirm that I have obtained consent from the member contact to
              send email communications from the {noun.Singular}, including updates,
              event notifications, and member benefits, to the contacts
              provided. I have made the company aware they can unsubscribe at
              any time.{(confirmsSavedMembership || isCreateFlow) && (
                <> <span className="text-[#E24C4C]">*</span></>
              )}
            </span>
          }
        />
        {errors.emailCommunicationConsentConfirmed && (
          <FormHelperText error>
            {errors.emailCommunicationConsentConfirmed}
          </FormHelperText>
        )}
      </div>

      <section
        id="total-new-member-invoice"
        className="border-b border-gray-200 pb-10 pt-8 text-black"
      >
        {/* Spec §8: add-ons now go on a SEPARATE full-amount invoice on EVERY
          schedule, so any selected add-ons split the totals into Membership
          Invoice + Add-on Benefits Invoice with the separate-billing callout
          (the old combined annual figure and its "separate line item on
          invoice" caption are gone — no longer true). */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-[minmax(0,1fr)_260px] md:items-center">
          <div>
            <h3 className="text-3xl font-bold leading-tight">
              {renewal ? "Total Membership Renewal Invoice" : membershipAdjustment && !isReadOnlyMembershipDetails
                ? "Estimated Membership Billing"
                : "Total New Member Invoice"}
            </h3>
            {renewal ? (
              <p className="mt-3 text-lg leading-7">
                {isMonthlySchedule
                  ? "This renewal creates one invoice for the monthly membership amount. The renewal date advances by one month after payment."
                  : "The amount below will be the total amount invoiced to this member plus any applicable tax."}
                {" "}Renewal invoices include membership only. Add-on benefits are billed separately.
              </p>
            ) : isStandaloneReadOnlyMembershipDetails ? (
              <p className="mt-3 text-lg leading-7">
                The amount below will be the total amount invoiced to this
                member plus any applicable tax.
                <br />
                Add-on benefits are billed separately from membership.
              </p>
            ) : isReadOnlyMembershipDetails ? (
              <p className="mt-3 text-lg leading-7">
                These amounts come from the member's saved membership details.
              </p>
            ) : membershipAdjustment ? (
              <p className="mt-3 text-lg leading-7">
                This estimate does not include add-ons or the onboarding fee.
                When you save, we check the price and payment history. Then we
                apply the change now or at the next renewal.
              </p>
            ) : isMonthlySchedule ? (
              <p className="mt-3 text-lg leading-7">
                {membershipInvoiceTotalInDollars === 0
                  ? "The membership invoice has no amount due. No monthly payments are required."
                  : usesThirdPartyBilling
                  ? "Billed on the selected monthly schedule to the responsible third party; the member is not asked for a credit card."
                  : monthlyPaymentsAvailable
                    ? "Invoiced once for the full membership total; paid in 12 monthly credit-card installments; each payment gets its own receipt."
                    : `This member pays monthly, but credit card payments are turned off for your ${noun.singular}. Select Yearly before you save, or turn on Credit Card in ${noun.Singular} Set Up > Global Settings > Payment Options.`}
              </p>
            ) : (
              <p className="mt-3 text-lg leading-7">
                The amount below will be the total amount invoiced to this
                member plus any applicable tax.
              </p>
            )}
            {showSeparateAddOnInvoice && (
              <p className="mt-2 text-lg leading-7">
                The additional membership benefit add-ons will be invoiced
                separately from membership.
              </p>
            )}
          </div>
          {showSeparateAddOnInvoice ? (
            <div className="space-y-4 md:text-right">
              <div>
                <p className="text-base font-bold">Membership Invoice</p>
                <p className="text-3xl font-bold">
                  {membershipInvoiceTotalLabel}
                </p>
              </div>
              <div>
                <p className="text-base font-bold">Add-on Benefits Invoice</p>
                <p className="text-3xl font-bold">{addOnInvoiceTotalLabel}</p>
              </div>
            </div>
          ) : (
            <p className="text-4xl font-bold md:text-right">
              {renewal?.previewTotal || (renewal ? membershipInvoiceTotalLabel : membershipAdjustment
                ? membershipInvoiceTotalLabel
                : isMonthlySchedule
                  ? membershipInvoiceTotalLabel
                  : invoiceTotalLabel)}
            </p>
          )}
        </div>
      </section>
    </BillingRoot>
  );
};

export default MemberBillingStep;
